import { print } from 'graphql'
import { graphql } from 'app/generated/gql'
import type { Client } from '@urql/core'

import type { Message as MessageType, MessageListResult, SendContactEmailInput } from 'app/generated/graphql'
import { EMAIL_NETWORK_FAILURE, emailRefusalReason } from 'lib/emailAttachments'
import { excludeHeld } from 'app/lib/heldMessages'
import { modelData } from 'app/lib/modelData'
import { AUTH_ERROR_MESSAGES, redirectToLogin } from 'app/lib/urqlAuthExchange'
import { API_URL } from 'app/lib/urqlClient'
import { logError } from 'lib/logging'
import { EntityStore, type ListParams } from './EntityStore'

const MESSAGE_FIELDS = `aiWorkflowId aiWorkflowName isInitialMessage archived attachments { filename contentType sizeBytes } callerId channel contactId createdDate errorCode errorMessage from htmlBody id isAgent locationId orgId providerMessageId read sentByUserId status subject text threadId to updatedDate version workspaceId`

const LIST_QUERY = graphql(`
  query Messages($search: String, $pagination: PaginationInput, $sort: SortInput) {
    messages(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        aiWorkflowId
        aiWorkflowName
        isInitialMessage
        archived
        attachments {
          filename
          contentType
          sizeBytes
        }
        callerId
        channel
        contactId
        createdDate
        errorCode
        errorMessage
        from
        htmlBody
        id
        isAgent
        locationId
        orgId
        providerMessageId
        read
        sentByUserId
        status
        subject
        text
        threadId
        to
        updatedDate
        version
        workspaceId
      }
      total
      page
    }
  }
`)

const GET_QUERY = graphql(`
  query Message($id: ID!) {
    message(id: $id) {
      aiWorkflowId
      aiWorkflowName
      isInitialMessage
      archived
      attachments {
        filename
        contentType
        sizeBytes
      }
      callerId
      channel
      contactId
      createdDate
      errorCode
      errorMessage
      from
      htmlBody
      id
      isAgent
      locationId
      orgId
      providerMessageId
      read
      sentByUserId
      status
      subject
      text
      threadId
      to
      updatedDate
      version
      workspaceId
    }
  }
`)

export const SEND_CONTACT_EMAIL_MUTATION = graphql(`
  mutation SendContactEmail($input: SendContactEmailInput!) {
    sendContactEmail(input: $input) {
      id
      orgId
      workspaceId
      locationId
      threadId
      from
      to
      sentByUserId
      isAgent
      contactId
      channel
      subject
      htmlBody
      text
      attachments {
        filename
        contentType
        sizeBytes
      }
      read
      archived
      createdDate
      updatedDate
      status
      providerMessageId
      errorCode
      errorMessage
    }
  }
`)

// No updateMutation: the API replaced `updateMessage(id, input)` with the
// per-purpose mutations in app/graphql/messageMutations.ts, whose flat args
// don't fit EntityStore's fixed { id, input } variable shape. Callers mutate
// via gqlMutation with those documents and use this store for reads.
export class MessageStore extends EntityStore<MessageType, MessageListResult> {
  constructor(client: Client) {
    super(client, {
      listQuery: LIST_QUERY,
      getQuery: GET_QUERY,
      extractList: (data: any) => data.messages,
      extractOne: (data: any) => data.message,
    })
  }

  // Held ("send later") messages are ordinary indexed rows the resolver does
  // not filter out on its own. This store backs generic message reads, not the
  // scheduled-messages tray, so every list fetch excludes them here.
  fetchList(
    params: ListParams = {},
    opts?: { requestPolicy?: 'cache-first' | 'cache-and-network' | 'network-only' | 'cache-only' },
  ) {
    return super.fetchList({ ...params, search: excludeHeld(params.search) }, opts)
  }

  // XHR rather than urql: fetch has no upload progress, and attachments make
  // this the one request large enough to need it.
  sendEmail(
    input: SendContactEmailInput,
    onProgress?: (fraction: number) => void,
  ): Promise<{ ok: true; message: MessageType } | { ok: false; code?: string; reason: string }> {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', API_URL)
      xhr.withCredentials = true
      xhr.setRequestHeader('Content-Type', 'application/json')
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) onProgress?.(evt.loaded / evt.total)
      }
      xhr.onerror = () => resolve({ ok: false, reason: EMAIL_NETWORK_FAILURE })
      xhr.onload = () => {
        if (xhr.status === 401) {
          redirectToLogin()
          return resolve({ ok: false, reason: emailRefusalReason(undefined, undefined) })
        }
        try {
          const body = JSON.parse(xhr.responseText)
          const error = body.errors?.[0]
          if (body.errors?.some((e: { message?: string }) => AUTH_ERROR_MESSAGES.has(e.message ?? '')))
            redirectToLogin()
          if (error) {
            const code = error.extensions?.code
            return resolve({ ok: false, code, reason: emailRefusalReason(code, error.message) })
          }
          const message = this.intern(modelData(body.data.sendContactEmail) as MessageType)
          if (!message) throw new Error('sendContactEmail returned no message')
          resolve({ ok: true, message })
        } catch (error) {
          logError('sendContactEmail response unreadable', error)
          resolve({ ok: false, reason: `Unexpected response (${xhr.status}).` })
        }
      }
      xhr.send(JSON.stringify({ query: print(SEND_CONTACT_EMAIL_MUTATION), variables: { input } }))
    })
  }
}
